<template>
	<v-dialog
		persistent
		v-model="show"
		max-width="700"
	>
		<v-card>
			<v-card-title class="success white--text">
				Export in a Table
			</v-card-title>
			<v-progress-linear
				v-if="downloading"
				indeterminate
				class="ma-0"
				style="position: absolute;"
			/>
			<v-card-text style="margin-top: 7px;">
				<v-layout>
					<v-flex>
						<p class="mb-2">Choose fields to export to a table:</p>
						<v-checkbox
							:input-value="allSelected"
							label="Select all / Deselect all"
							color="primary"
							class="ma-0 mb-2"
							style="font-weight: bold;"
							hide-details
							:disabled="downloading"
							@change="toggleSelectAll"
						/>
					</v-flex>
					<v-flex>
						<p class="mb-2">Choose table format:</p>
						<v-radio-group
							v-model="tableFormat"
							hide-details
							row
							class="mt-0 mb-3"
						>
							<v-radio
								v-for="format in tableFormats"
								:key="format.value"
								:label="format.label"
								:value="format.value"
								color="primary"
							/>
						</v-radio-group>
					</v-flex>
				</v-layout>
				<draggable
					:list="fields"
					class="draggable mt-3"
					:style="{ height: draggableHeight + 'px' }"
				>
					<div
						v-for="f in fields"
						:key="f.attr"
						class="px-2 py-1"
						style="width: 50%;"
					>
						<v-card class="pa-1 pr-2">
							<v-layout>
								<v-checkbox
									v-model="f.selected"
									:label="f.attr === 'publicationName' ? 'Publication Name' : f.label"
									name="fields"
									hide-details
									:disabled="downloading"
									color="primary"
									class="ma-0"
								/>
								<v-icon small>fa-sort</v-icon>
							</v-layout>
						</v-card>
					</div>
				</draggable>
			</v-card-text>
			<v-card-text>
				<v-textarea
					v-if="showCompiledUrl"
					outline
					label="Url"
					hide-details
					:value="compiledUrl"
					readonly
					no-resize
					ref="compiledUrl"
					class="compiled-url-container"
				>
					<template slot="append">
						<v-tooltip top>
							<template slot="activator">
								<v-btn
									flat
									icon
									@click="copyToClipboard"
								>
									<v-icon>file_copy</v-icon>
								</v-btn>
							</template>
							Copy to clipboard
						</v-tooltip>
					</template>
				</v-textarea>
			</v-card-text>
			<v-divider />
			<v-card-actions>
				<v-spacer />
				<v-btn
					flat
					color="success"
					:disabled="!validChoice || downloading"
					@click="download"
				>
					{{ tableFormat === 'table' ? 'Open' : 'Export' }}
				</v-btn>
				<v-btn
					flat
					color="primary"
					:disabled="!validChoice || downloading"
					@click="showCompiledUrl = !showCompiledUrl"
				>
					<template v-if="showCompiledUrl">Hide</template>
					<template v-else>Show</template>
					Link
				</v-btn>
				<v-btn
					flat
					color="error"
					:disabled="downloading"
					@click="show = false"
				>
					Cancel
				</v-btn>
			</v-card-actions>
		</v-card>
		<iframe
			ref="downloadIframe"
			style="display: none;"
		/>
	</v-dialog>
</template>

<script>
import draggable from 'vuedraggable';

export default {
	components: {
		draggable,
	},
	props: {
		output: { type: Object, default: {} },
		outputKey: { type: String, default: '' }
	},
	data() {
		return {
			show: false,
			downloading: false,
			showCompiledUrl: false,
			tableFormat: 'excel',
			tableFormats: [
				{ value: 'excel', label: 'Excel' },
				{ value: 'table', label: 'HTML Table' },
				{ value: 'csv', label: 'CSV' },
			],
			fields: [],
			allProjectFields: [],
			savedFields: [],
		};
	},
	computed: {
		currentProject() {
			return this.$store.get('projects/currentProject');
		},
		baseUrl() {
			if (this.currentProject.customDomain) {
				return 'https://' + this.currentProject.customDomain;
			}

			return process.env.VUE_APP_OUTPUT_ROOT;
		},
		draggableHeight() {
			// Adjust fields container height
			// 44px - height of one field card
			return Math.ceil(this.fields.length / 2) * 44;
		},
		markdownOptions() {
			return this.$store.get('markdownOptions');
		},
		selectedFields() {
			return this.fields.filter(f => f.selected);
		},
		selectedAttrs() {
			return this.selectedFields.map(f => f.attr);
		},
		validChoice() {
			return this.selectedFields.length > 0;
		},
		allSelected() {
			return this.selectedFields.length === this.fields.length;
		},
		tableFormatName() {
			return this.tableFormats.find(f => f.value === this.tableFormat).label;
		},
		compiledUrl() {
			const url = `${this.baseUrl}/${this.tableFormat}/${this.outputKey}`;

			if (this.selectedFields.length === 0)
				return url;

			return url + '?fields=' + this.selectedAttrs.join(',');
		},
	},
	watch: {
		validChoice(newVal) {
			if (newVal === false)
				this.showCompiledUrl = false;
		},
		markdownOptions(newVal) {
			//console.log('markdownOptions updated');
			
			let fields = [];
			
			newVal.filter(type => type.articleRelated)
				.forEach(type => {
					type.options
						.filter(f => f.export && !!f.attr)
						.forEach(f => {
							if (fields.find(field => field.attr === f.attr))
								return;
							
							fields.push({
								attr: f.attr,
								label: f.name,
								selected: true,
							});
						});
				});

			this.allProjectFields = fields;
			
			// if it's already set when loaded, it won't trigger the watch,
			// so load if output is set and saved is empty
			if (!!this.output && this.savedFields.length === 0) {
				//console.log(['output', this.output])
				this.savedFields = this.loadFieldOrder();
			}
			
			//console.log('markdownOptions updateFields');
			// always update when markdown updates
			this.updateFields();
		},
		output(newVal) {
			//console.log('output updated');
			
			if (!newVal) {
				return;
			}
			
			this.savedFields = this.loadFieldOrder();
			
			// only update if the default ones have been loaded
			if (this.allProjectFields.length > 0) {
				//console.log('output updateFields');
				this.updateFields();
			}
		},
		fields: {
			handler(newVal) {
				this.saveFieldOrder(newVal);
			},
			deep: true,
		},
	},
	methods: {
		toggleSelectAll(selectAll) {
			this.fields.map(f => f.selected = selectAll);
		},
		open() {
			this.show = true;
		},
		copyToClipboard() {
			this.$refs.compiledUrl.$refs.input.select();
			try {
				document.execCommand('copy');
				this.$notify('success', 'Link copied to clipboard.');
			}
			catch (e) {
				this.$notify('error', 'Link has not been copied to clipboard due to some error.');
			}
		},
		download() {
			if (this.tableFormat === 'table') {
				window.open(this.compiledUrl, '_blank');
				return;
			}

			this.$refs.downloadIframe.src = this.compiledUrl;
			this.$notify('info', `${this.tableFormatName} file is preparing for downloading.`);
			this.show = false;
		},
		loadFieldOrder() {
			//return this.$ls.get('excel-fields-project-' + this.currentProject.id);
			return this.output.tableFields;
		},
		saveFieldOrder(fields) {
			//this.$ls.set('excel-fields-project-' + this.currentProject.id, fields);
			this.output.tableFields = fields;
			this.output.save();
		},
		updateFields() {
			let fields = this.savedFields;

			// add on default ones at the end of the saved ones
			this.allProjectFields.forEach(f => {
				let existing = fields.find(field => field.attr === f.attr);
				if (!!existing) {
					existing.label = f.label; // just in case the label changed
					return;
				}

				fields.push({
					attr: f.attr,
					label: f.label,
					selected: true,
				});
			})
			
			this.fields = fields;
		},
	}
};
</script>

<style lang="scss" scoped>
.draggable {
	display: flex;
	flex-wrap: wrap;
	flex-direction: column;
	margin: 0 -8px;
}
</style>
<style lang="scss">
.compiled-url-container {
	.v-input__append-inner {
		margin-top: 0 !important;
		margin-right: -12px !important;
		padding-left: 12px !important;
	}
}
</style>
